import type { ActionReducerMapBuilder } from '@reduxjs/toolkit'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type {
  EnhancedConsequencesOfChange,
  EnhancedProductFamilyId,
  EnhancedProductVariantId,
  EnhancedVariantConsequence,
  EnhancedVariantConsequences,
} from '#api/enhancements/enhancedTmsApiTypes'
import type { ConsequencesOfChange, EcmConfiguratorStatus } from '#api/tmsApiService'

import type { TruckConfiguration, TruckConfigurations } from '../slices/configurationSlice'

export interface SelectionConflict {
  conflicts: {
    request: {
      id: string
      requestedChange: string | undefined
    }
    userChoice: Selection
    resolution: Omit<Selection, 'variant'> & Partial<Pick<Selection, 'variant'>>
  }[]
  consequences: ConsequencesOfChange
}

interface Selection {
  family: EnhancedProductFamilyId
  variant: EnhancedProductVariantId
}

const handleConsequence = (
  selections: TruckConfiguration['configuredFamilies'],
  consequence: EnhancedVariantConsequence,
  asConflict: boolean,
):
  | undefined
  | {
      type: 'conflict' | 'change'
      family: EnhancedProductFamilyId
      fromVariant: EnhancedProductVariantId
      toVariant: EnhancedProductVariantId | undefined
    } => {
  const familyId = consequence.productFamily?.id
  const variantId = consequence.currentVariant?.id

  // no explicit choice also means no explicit
  if (!familyId) {
    return
  }
  const selection = selections[familyId]
  const selectedVariant = selection?.selectedVariant
  // if there's no user selection or no explicit variant has been selected, ignore it
  if (!selection || !selectedVariant) {
    return
  }
  switch (consequence.variantStatus) {
    case undefined:
      break
    // in case Added | Changed + a selection existed: raise a ticket
    case 'Added':
    case 'Changed': {
      if (!variantId) {
        break
      }
      return asConflict
        ? {
            type: 'conflict',
            family: familyId,
            fromVariant: selectedVariant.id,
            toVariant: variantId,
          }
        : {
            type: 'change',
            family: familyId,
            fromVariant: selectedVariant.id,
            toVariant: variantId,
          }
    }
    // if it's being removed, that's an explicit error
    case 'Removed': {
      return {
        type: 'conflict',
        family: familyId,
        fromVariant: selectedVariant.id,
        toVariant: variantId,
      }
    }
  }
}

function handleConsequencesOfChanges(
  requestId: string,
  requestedChange: string | undefined,
  configuration: TruckConfiguration,
  consequencesOfChange: EnhancedConsequencesOfChange,
) {
  const { variantConsequences = [], variantConflictingChoices = [] } = consequencesOfChange

  const conflicts: SelectionConflict['conflicts'] = []
  const handleConsequences = (consequences: EnhancedVariantConsequences, asConflict: boolean) => {
    for (const consequence of consequences) {
      const result = handleConsequence(configuration.configuredFamilies, consequence, asConflict)
      if (!result) {
        continue
      }

      if (result.type === 'conflict') {
        conflicts.push({
          request: {
            id: requestId,
            requestedChange,
          },
          userChoice: {
            family: result.family,
            variant: result.fromVariant,
          },
          resolution: {
            family: result.family,
            variant: result.toVariant,
          },
        })
        // continue
      }

      /*
        20245-04-03:
        @FIXME?: this may be a problem, this used to overwrite the user selection whenever a request was fulfilled to fix issues with conflict tracking
                 however, this also causes an issue that selections see the wrong variant selection when changing the entry.
                 if you run into conflict errors, then this may be the reason why
      // explicit checking so that it's not breaking when the return types of `handleConsequence` change
      if (result.type === 'change') {
        configuration.configuredFamilies[result.family] = {
          printOptions: {},
          isLocked: false,
          ...configuration.configuredFamilies[result.family],
          selectedVariant
        }
      }
      */
    }
  }

  handleConsequences(variantConsequences, false)
  handleConsequences(variantConflictingChoices, true)
}

export const addConflictErrorTracker = (builder: ActionReducerMapBuilder<TruckConfigurations>) => {
  ;[
    enhancedTmsApiService.endpoints.getApiConfigurationByUuid,
    enhancedTmsApiService.endpoints.postApiConfiguration,
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidSelectVariantAndVariantId,
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidStepAndStep,
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidUndocommit,
  ]
    .map((x) => x.matchFulfilled)
    .map((x) =>
      builder.addMatcher(x, (state, action) => {
        const configuration = Object.values(state.configurations).find((x) => x?.uuid === action.meta.arg.originalArgs.uuid)
        if (!configuration) {
          return
        }

        switch (action.meta.arg.endpointName) {
          case undefined:
            break
          case 'getConfigurationByUuid':
          case 'postConfigurationByUuidSelectVariantAndVariantId':
          case 'postConfigurationByUuidStepAndStep':
          case 'postConfigurationByUuidUndocommit': {
            if ('state' in action.payload) {
              configuration.lastEcmState = action.payload.state as EcmConfiguratorStatus | undefined
            }
          }
        }

        switch (action.meta.arg.endpointName) {
          case undefined:
            break
          case 'postConfiguration':
          case 'postConfigurationByUuidSelectVariantAndVariantId':
          case 'postConfigurationByUuidStepAndStep':
          case 'postConfigurationByUuidUndocommit': {
            if (action.payload.consequencesOfChange) {
              const requestedChange =
                'variant-id' in action.meta.arg.originalArgs ? (action.meta.arg.originalArgs['variant-id'] as string) : undefined
              handleConsequencesOfChanges(action.meta.requestId, requestedChange, configuration, action.payload.consequencesOfChange)
            }
          }
        }
      }),
    )
}
